import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Box, Button, Chip, CircularProgress, Paper, Snackbar, Alert, Grid, Typography } from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import DownloadIcon from '@mui/icons-material/Download';
import EuroIcon from '@mui/icons-material/Euro';
import GroupsIcon from '@mui/icons-material/Groups';
import PaidIcon from '@mui/icons-material/Paid';
import ScienceIcon from '@mui/icons-material/Science';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { usePaginatedList } from '../../hooks/usePaginatedList';
import {
  useListPreferences,
  FilterButton,
  FilterPanel,
  FilterSection,
  FilterChip,
  SearchInput,
  FilterSummary,
} from '../shared/ListFilters';
import DataTable from '../shared/DataTable';
import PaginationControls from '../shared/PaginationControls';
import MetricCard from '../reports/components/MetricCard';
import OrganizationDetailDialog from './OrganizationDetailDialog';

const STORAGE_KEY = 'vadmin_organizations_preferences';

const defaultPreferences = {
  sortField: 'created_at',
  sortDirection: 'desc',
  searchQuery: '',
  filters: {
    subscription_status: '',
  },
};

const STATUS_COLORS = {
  active: 'success',
  suspended: 'error',
  cancelled: 'error',
  none: 'default',
};

export default function OrganizationsTab() {
  const { t } = useTranslation();
  const [detailOrgId, setDetailOrgId] = useState(null);

  // ?org=<id> opens that organization's details (link in the data purge warning email).
  useEffect(() => {
    const orgId = Number(new URLSearchParams(window.location.search).get('org'));
    if (orgId) setDetailOrgId(orgId);
  }, []);

  const {
    preferences,
    updatePreferences,
    updateFilters: updateLocalFilters,
    resetFilters: resetLocalFilters,
    filtersOpen,
    toggleFiltersOpen,
  } = useListPreferences(STORAGE_KEY, defaultPreferences);

  const filters = preferences.filters;

  const {
    data: organizations,
    loading,
    error,
    page,
    pageSize,
    totalCount,
    totalPages,
    setPage,
    setPageSize,
    setSearchQuery,
    setFilters: setServerFilters,
    sortField,
    sortDirection,
    handleSort,
  } = usePaginatedList('/api/vadmin/organizations/', {
    initialFilters: filters,
    defaultSortField: preferences.sortField,
    defaultSortDirection: preferences.sortDirection,
    defaultPageSize: 50,
    initialSearchQuery: preferences.searchQuery || '',
  });

  // KPI stats
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const statsRequestId = useRef(0);

  // Build query params for stats (same filters as list)
  const statsQueryParams = useMemo(() => {
    const params = new URLSearchParams();
    if (preferences.searchQuery) {
      params.set('search', preferences.searchQuery);
    }
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '' && value !== 'all') {
        params.set(key, String(value));
      }
    });
    return params.toString();
  }, [filters, preferences.searchQuery]);

  useEffect(() => {
    const currentId = ++statsRequestId.current;
    setStatsLoading(true);
    const url = statsQueryParams
      ? `/api/vadmin/organizations/stats/?${statsQueryParams}`
      : '/api/vadmin/organizations/stats/';
    axios.get(url)
      .then((response) => {
        if (currentId === statsRequestId.current) {
          setStats(response.data);
          setStatsLoading(false);
        }
      })
      .catch(() => {
        if (currentId === statsRequestId.current) {
          setStatsLoading(false);
        }
      });
  }, [statsQueryParams]);

  // Sync local filters to server
  useEffect(() => {
    setServerFilters(filters);
  }, [filters, setServerFilters]);

  useEffect(() => {
    setSearchQuery(preferences.searchQuery || '');
  }, [preferences.searchQuery, setSearchQuery]);

  const updateFilters = useCallback((newFilters) => {
    updateLocalFilters(newFilters);
  }, [updateLocalFilters]);

  const resetFilters = useCallback(() => {
    resetLocalFilters();
  }, [resetLocalFilters]);

  const handleStatusFilter = useCallback((status) => {
    updateFilters({
      subscription_status: filters.subscription_status === status ? '' : status,
    });
  }, [filters.subscription_status, updateFilters]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.subscription_status) count++;
    return count;
  }, [filters]);

  const filterSummaryChips = useMemo(() => {
    const chips = [];
    if (filters.subscription_status) {
      const statusLabels = {
        active: t('vadmin.active'),
        trial: t('vadmin.trial'),
        cancelling: t('vadmin.cancelling'),
        suspended: t('vadmin.suspended'),
        cancelled: t('vadmin.cancelled'),
        none: t('vadmin.noSubscription'),
      };
      chips.push({
        key: 'status',
        label: statusLabels[filters.subscription_status] || filters.subscription_status,
      });
    }
    return chips;
  }, [filters, t]);

  // Error snackbar
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
  const prevError = useRef(error);
  useEffect(() => {
    if (error && error !== prevError.current) {
      setShowErrorSnackbar(true);
    }
    prevError.current = error;
  }, [error]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('fi-FI');
  };

  const getStatusChip = (row) => {
    const status = row.subscription_status;

    // A scheduled cancellation keeps the status ACTIVE until the end date, so
    // without this the client looks identical to a healthy one in the list.
    const pendingCancellation =
      status === 'active' &&
      row.subscription_end &&
      new Date(row.subscription_end) > new Date();

    const cancellingChip = pendingCancellation ? (
      <Chip
        label={t('vadmin.endsOn', { date: formatDate(row.subscription_end) })}
        size="small"
        color="warning"
        variant="outlined"
        sx={{ fontWeight: 500, fontSize: '12px' }}
      />
    ) : null;

    if (status === 'active' && row.is_trial) {
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          <Chip
            label={t('vadmin.trial')}
            size="small"
            color="warning"
            variant="filled"
            sx={{ fontWeight: 500, fontSize: '12px' }}
          />
          {cancellingChip}
        </Box>
      );
    }
    const label = {
      active: t('vadmin.active'),
      suspended: t('vadmin.suspended'),
      cancelled: t('vadmin.cancelled'),
      none: t('vadmin.noSubscription'),
    }[status] || status;

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        <Chip
          label={label}
          size="small"
          color={STATUS_COLORS[status] || 'default'}
          variant={status === 'none' ? 'outlined' : 'filled'}
          sx={{ fontWeight: 500, fontSize: '12px' }}
        />
        {cancellingChip}
      </Box>
    );
  };

  const getTrialInfo = (row) => {
    if (!row.is_trial) return '—';
    if (row.trial_days_remaining > 0) {
      return t('vadmin.trialDaysLeft', { days: row.trial_days_remaining });
    }
    return t('vadmin.trialExpired');
  };

  const getSeatsDisplay = (row) => {
    if (row.subscription_status === 'none') return '—';
    if (row.seat_limit === 0) return `${row.user_count} / ${t('vadmin.unlimited')}`;
    return `${row.user_count} / ${row.seat_limit}`;
  };

  const getBillingDisplay = (row) => {
    if (!row.billing_period) return '—';
    return row.billing_period === 'monthly' ? t('vadmin.monthly') : t('vadmin.yearly');
  };

  const getPaymentMethodDisplay = (row) => {
    if (!row.payment_method) return '—';
    return row.payment_method === 'invoice' ? t('vadmin.invoice') : 'Stripe';
  };

  const handleCsvExport = useCallback(() => {
    const params = new URLSearchParams();
    if (preferences.searchQuery) params.set('search', preferences.searchQuery);
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '' && value !== 'all') {
        params.set(key, String(value));
      }
    });
    const queryStr = params.toString();
    const url = queryStr
      ? `/api/vadmin/organizations/csv-export/?${queryStr}`
      : '/api/vadmin/organizations/csv-export/';
    window.location.href = url;
  }, [preferences.searchQuery, filters]);

  const columns = [
    {
      key: 'name',
      label: t('vadmin.organization'),
      sortable: true,
      maxWidth: 250,
      render: (row) => row.name,
    },
    {
      key: 'business_id',
      label: t('vadmin.businessId'),
      width: '1%',
      noWrap: true,
      render: (row) => row.business_id || '—',
    },
    {
      key: 'admin_email',
      label: t('vadmin.adminEmail'),
      hideBelow: 'xl',
      maxWidth: 200,
      render: (row) => row.admin_email || '—',
    },
    {
      key: 'phone',
      label: t('vadmin.phone'),
      hideBelow: 'xl',
      width: '1%',
      noWrap: true,
      render: (row) => row.phone || '—',
    },
    {
      key: 'plan_name',
      label: t('vadmin.plan'),
      sortable: true,
      sortField: 'sort_plan_name',
      render: (row) => row.plan_name || '—',
    },
    {
      key: 'subscription_status',
      label: t('vadmin.status'),
      sortable: true,
      sortField: 'sort_subscription_status',
      render: (row) => getStatusChip(row),
    },
    {
      key: 'trial_info',
      label: t('vadmin.trial'),
      hideBelow: 'lg',
      render: (row) => getTrialInfo(row),
    },
    {
      key: 'billing_period',
      label: t('vadmin.billing'),
      hideBelow: 'lg',
      render: (row) => getBillingDisplay(row),
    },
    {
      key: 'payment_method',
      label: t('vadmin.paymentMethod'),
      hideBelow: 'xl',
      render: (row) => getPaymentMethodDisplay(row),
    },
    {
      key: 'user_count',
      label: t('vadmin.users'),
      width: '1%',
      noWrap: true,
      render: (row) => getSeatsDisplay(row),
    },
    {
      key: 'created_at',
      label: t('vadmin.created'),
      sortable: true,
      width: '1%',
      noWrap: true,
      render: (row) => formatDate(row.created_at),
    },
  ];

  return (
    <Box>
      <Snackbar
        open={showErrorSnackbar && !!error}
        autoHideDuration={6000}
        onClose={() => setShowErrorSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowErrorSnackbar(false)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={EuroIcon}
            label={t('vadmin.mrrLabel')}
            value={stats?.mrr}
            loading={statsLoading}
            format="currency"
            iconColor="success.main"
            iconBgColor="rgba(46, 125, 50, 0.1)"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={GroupsIcon}
            label={t('vadmin.totalClients')}
            value={stats?.total_clients}
            loading={statsLoading}
            format="number"
            iconColor="secondary.main"
            iconBgColor="rgba(49, 130, 206, 0.1)"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={PaidIcon}
            label={t('vadmin.payingClients')}
            value={stats?.paying_clients}
            loading={statsLoading}
            format="number"
            iconColor="primary.main"
            iconBgColor="rgba(21, 101, 192, 0.1)"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={ScienceIcon}
            label={t('vadmin.activeTrials')}
            value={stats?.active_trials}
            loading={statsLoading}
            format="number"
            iconColor="warning.main"
            iconBgColor="rgba(237, 108, 2, 0.1)"
          />
        </Grid>
        {/* Only when there is churn to look at, so the 4-card row stays intact otherwise */}
        {stats?.cancelling_clients > 0 && (
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              icon={EventBusyIcon}
              label={t('vadmin.cancellingClients')}
              value={stats.cancelling_clients}
              loading={statsLoading}
              format="number"
              iconColor="error.main"
              iconBgColor="rgba(211, 47, 47, 0.1)"
            />
          </Grid>
        )}
      </Grid>

      {/* Search and Filter Row */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          mb: 2.5,
          alignItems: { xs: 'stretch', sm: 'center' },
        }}
      >
        <SearchInput
          value={preferences.searchQuery || ''}
          onChange={(value) => updatePreferences({ searchQuery: value })}
          onClear={() => updatePreferences({ searchQuery: '' })}
          placeholder={t('vadmin.searchPlaceholder')}
        />
        <FilterButton
          t={t}
          activeCount={activeFilterCount}
          isOpen={filtersOpen}
          onClick={toggleFiltersOpen}
        />
        <Button
          variant="outlined"
          size="small"
          startIcon={<DownloadIcon />}
          onClick={handleCsvExport}
          sx={{ whiteSpace: 'nowrap' }}
        >
          {t('vadmin.csvExport')}
        </Button>
      </Box>

      <FilterSummary chips={filterSummaryChips} onClear={resetFilters} t={t} />

      <FilterPanel
        open={filtersOpen}
        resultCount={totalCount}
        onReset={resetFilters}
        resetDisabled={activeFilterCount === 0}
        t={t}
      >
        <FilterSection title={t('vadmin.status')}>
          <FilterChip
            label={t('vadmin.active')}
            active={filters.subscription_status === 'active'}
            onClick={() => handleStatusFilter('active')}
            color="success"
          />
          <FilterChip
            label={t('vadmin.trial')}
            active={filters.subscription_status === 'trial'}
            onClick={() => handleStatusFilter('trial')}
            color="warning"
          />
          <FilterChip
            label={t('vadmin.cancelling')}
            active={filters.subscription_status === 'cancelling'}
            onClick={() => handleStatusFilter('cancelling')}
            color="warning"
          />
          <FilterChip
            label={t('vadmin.suspended')}
            active={filters.subscription_status === 'suspended'}
            onClick={() => handleStatusFilter('suspended')}
            color="error"
          />
          <FilterChip
            label={t('vadmin.cancelled')}
            active={filters.subscription_status === 'cancelled'}
            onClick={() => handleStatusFilter('cancelled')}
            color="error"
          />
          <FilterChip
            icon={<BusinessIcon />}
            label={t('vadmin.noSubscription')}
            active={filters.subscription_status === 'none'}
            onClick={() => handleStatusFilter('none')}
            color="default"
          />
        </FilterSection>
      </FilterPanel>

      <DataTable
        columns={columns}
        data={organizations}
        loading={loading}
        onRowClick={(org) => setDetailOrgId(org.id)}
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={(field) => {
          handleSort(field);
          updatePreferences((prev) => ({
            ...prev,
            sortField: field,
            sortDirection: prev.sortField === field && prev.sortDirection === 'asc' ? 'desc' : 'asc',
          }));
        }}
        emptyIcon={<BusinessIcon sx={{ fontSize: 48 }} />}
        emptyTitle={t('vadmin.noOrganizations')}
        emptyDescription={t('vadmin.noOrganizationsDescription')}
        pagination={{
          page,
          totalPages,
          totalCount,
          pageSize,
          onPageChange: setPage,
          onPageSizeChange: setPageSize,
        }}
        testIdPrefix="vadmin-org"
        horizontalScroll
      />

      {/* Mobile Cards - Hidden on desktop */}
      <Box sx={{ display: { xs: 'block', lg: 'none' }, position: 'relative' }}>
        {loading && organizations.length > 0 && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(255, 255, 255, 0.7)',
              zIndex: 1,
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'center',
              pt: 8,
            }}
          >
            <CircularProgress size={32} />
          </Box>
        )}
        {loading && organizations.length === 0 ? (
          <Box sx={{ p: 6, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={32} />
          </Box>
        ) : organizations.length === 0 ? (
          <Paper elevation={0} sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              {t('vadmin.noOrganizations')}
            </Typography>
          </Paper>
        ) : (
          <>
            {organizations.map((org) => (
              <Paper
                key={org.id}
                elevation={0}
                data-testid={`vadmin-org-mobile-${org.id}`}
                onClick={() => setDetailOrgId(org.id)}
                sx={{
                  p: 2,
                  mb: 1.5,
                  cursor: 'pointer',
                  transition: 'background-color 0.15s ease',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      sx={{
                        fontSize: '15px',
                        fontWeight: 600,
                        color: 'text.primary',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {org.name}
                    </Typography>
                    {org.business_id && (
                      <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                        {org.business_id}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ ml: 1, flexShrink: 0 }}>
                    {getStatusChip(org)}
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1 }}>
                  {org.plan_name && (
                    <Box>
                      <Typography sx={{ fontSize: '11px', color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        {t('vadmin.plan')}
                      </Typography>
                      <Typography sx={{ fontSize: '13px', color: 'text.primary' }}>
                        {org.plan_name}
                      </Typography>
                    </Box>
                  )}
                  {org.is_trial && (
                    <Box>
                      <Typography sx={{ fontSize: '11px', color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        {t('vadmin.trial')}
                      </Typography>
                      <Typography sx={{ fontSize: '13px', color: 'warning.main', fontWeight: 500 }}>
                        {getTrialInfo(org)}
                      </Typography>
                    </Box>
                  )}
                  {org.billing_period && (
                    <Box>
                      <Typography sx={{ fontSize: '11px', color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        {t('vadmin.billing')}
                      </Typography>
                      <Typography sx={{ fontSize: '13px', color: 'text.primary' }}>
                        {getBillingDisplay(org)}
                      </Typography>
                    </Box>
                  )}
                  <Box>
                    <Typography sx={{ fontSize: '11px', color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      {t('vadmin.users')}
                    </Typography>
                    <Typography sx={{ fontSize: '13px', color: 'text.primary' }}>
                      {getSeatsDisplay(org)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '11px', color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      {t('vadmin.created')}
                    </Typography>
                    <Typography sx={{ fontSize: '13px', color: 'text.primary' }}>
                      {formatDate(org.created_at)}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            ))}

            <PaginationControls
              page={page}
              totalPages={totalPages}
              totalCount={totalCount}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              loading={loading}
            />
          </>
        )}
      </Box>

      <OrganizationDetailDialog
        open={!!detailOrgId}
        onClose={() => setDetailOrgId(null)}
        organizationId={detailOrgId}
      />
    </Box>
  );
}
